import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useConversationSwitching } from "@/hooks/useConversationSwitching";
import {
  AI_AGENT,
  AI_MODEL,
  useAiActionStore,
} from "@/store/zustand/aiActionStore";
import { track } from "@/utils/events/tracker";
import {
  getUnexpiredToken,
  useActiveProject,
  useCanvasId,
  useUserId,
} from "@/utils/utils";
import ConfiguringAuthStep from "./ClaudeSetupSteps/ConfiguringAuthStep";
import InfoStep from "./ClaudeSetupSteps/InfoStep";
import OAuthAuthStep from "./ClaudeSetupSteps/OAuthAuthStep";
import SubscriptionCheckStep from "./ClaudeSetupSteps/SubscriptionCheckStep";
import { useSetupClaudeCode } from "./useSetupClaudeCode";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSetupComplete: () => void;
  isForChat?: boolean;
}

type ModalStep =
  | "info"
  | "subscription-check"
  | "configuring-auth"
  | "oauth-auth";

const ClaudeCodeSetupModal: React.FC<Props> = ({
  isOpen,
  onClose: rawOnClose,
  onSetupComplete,
  isForChat = false,
}) => {
  const { session } = useSession();
  const activeProject = useActiveProject();
  const canvasId = useCanvasId();
  const userId = useUserId();
  const projectId = activeProject?.id;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const traceIdRef = useRef<string | null>(null);

  const [modalStep, setModalStep] = useState<ModalStep>("info");
  const [detectedOAuthUrl, setDetectedOAuthUrl] = useState<string>();
  const [oauthCodeInput, setOauthCodeInput] = useState("");
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);

  const [error, setError] = useState<string>();

  const { switchToModelConversation } = useConversationSwitching();

  // Handle error state
  useEffect(() => {
    if (error) {
      // Track authentication failure
      if (userId && traceIdRef.current) {
        track("CLAUDE_CODE_AUTH", {
          traceId: traceIdRef.current,
          userId,
          event: "auth_error",
          projectId,
          error,
        });
      }

      setOauthCodeInput("");
    }
  }, [error, userId, projectId]);

  const cleanupEnvVariable = useCallback(async () => {
    if (!session || !activeProject?.id) return;

    try {
      const supabaseJWT = await getUnexpiredToken(session);
      if (!supabaseJWT) return;

      // Remove CLAUDE_CODE_OAUTH_TOKEN if it was set previously
      await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${
          activeProject.id
        }/envVariables`,
        { CLAUDE_CODE_OAUTH_TOKEN: null },
        { headers: { Authorization: `Bearer ${supabaseJWT}` } },
      );
    } catch (error) {
      console.error("Failed to cleanup env variable:", error);
    }
  }, [session, activeProject?.id]);

  const storeCredentialsToDatabase = useCallback(async () => {
    if (!session || !canvasId) return;

    try {
      const supabaseJWT = await session.getToken({
        template: "tempo-claude-code",
      });
      if (!supabaseJWT) return;

      // Call claude-code-server to store credentials to database
      // Use query parameter for authHeader like other claude-code endpoints
      await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/claude-code/storeCredentials?authHeader=Bearer ${encodeURIComponent(
          supabaseJWT,
        )}`,
        {},
      );
    } catch (error) {
      console.error("Failed to store credentials to database:", error);
      // Don't show error to user as this is not critical to the flow
    }
  }, [session, canvasId]);

  const handleGetStarted = useCallback(() => {
    // Generate traceId for this auth session
    if (!traceIdRef.current) {
      traceIdRef.current = uuidv4();
    }

    // Track auth started
    if (userId && traceIdRef.current) {
      track("CLAUDE_CODE_AUTH", {
        traceId: traceIdRef.current,
        userId,
        event: "auth_started",
        projectId,
      });
    }

    setModalStep("subscription-check");
  }, [userId, projectId]);

  const { startClaudeCodeSetup, sendCode, cancelSetup } = useSetupClaudeCode();

  const onClose = useCallback(() => {
    rawOnClose();
    cancelSetup();
    setModalStep("info");
    setOauthCodeInput("");
    setDetectedOAuthUrl(undefined);
    setIsSubmittingCode(false);
    setError(undefined);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    traceIdRef.current = null; // Reset traceId
  }, [rawOnClose, cancelSetup]);

  useEffect(() => {
    return () => {
      cancelSetup();
    };
  }, [cancelSetup]);

  // Handle completion
  const handleCompletion = useCallback(() => {
    // Track successful authentication
    if (userId && traceIdRef.current) {
      track("CLAUDE_CODE_AUTH", {
        traceId: traceIdRef.current,
        userId,
        event: "auth_success",
        projectId,
      });
    }

    // Clean up env variable if it was set previously
    // Note: As of October 2025, having the CLAUDE_CODE_OAUTH_TOKEN env variable set breaks the claude code SDK for Pro users.
    // TODO: Remove this when Claude Code SDK stabilizes.
    cleanupEnvVariable();

    // Store credentials to database for sharing across canvases
    storeCredentialsToDatabase();

    const { setAgent, setCodeGenerationModel } = useAiActionStore.getState();

    // Auto-switch to Claude Code model after successful authentication
    setAgent(AI_AGENT.CLAUDE_AGENT);
    setCodeGenerationModel(
      AI_MODEL.CLAUDE_4_6_SONNET,
      switchToModelConversation,
    );

    onSetupComplete();
    onClose();

    // Reset after a brief delay
    setTimeout(() => {
      setModalStep("info");
      setOauthCodeInput("");
      traceIdRef.current = null; // Reset traceId
    }, 100);
  }, [
    userId,
    projectId,
    onSetupComplete,
    onClose,
    switchToModelConversation,
    // Note: As of October 2025, having the CLAUDE_CODE_OAUTH_TOKEN env variable set breaks the claude code SDK for Pro users.
    // TODO: Remove this when Claude Code SDK stabilizes.
    cleanupEnvVariable, // Store credentials to database for sharing across canvases
    storeCredentialsToDatabase,
  ]);

  const handleSubscriptionYes = useCallback(async () => {
    setModalStep("configuring-auth");

    // Set timeout for the entire flow
    timeoutRef.current = setTimeout(() => {
      toast.error("Authentication setup timed out. Please try again.");
      onClose();
    }, 30000);

    try {
      const { loggedIn, redirect } = await startClaudeCodeSetup();

      clearTimeout(timeoutRef.current);

      if (!loggedIn) {
        setModalStep("oauth-auth");
        setDetectedOAuthUrl(redirect);
      } else {
        handleCompletion();
      }
    } catch (err) {
      clearTimeout(timeoutRef.current);
      setModalStep("oauth-auth");
      setError(
        err instanceof Error ? err.message : "Failed to start authentication",
      );
    }
  }, [onClose, startClaudeCodeSetup, handleCompletion]);

  const handleSubscriptionNo = useCallback(() => {
    window.open("https://claude.ai/upgrade", "_blank");
  }, []);

  const handleSubmitCode = useCallback(async () => {
    if (oauthCodeInput.trim()) {
      // Track OAuth code submission
      if (userId && traceIdRef.current) {
        track("CLAUDE_CODE_AUTH", {
          traceId: traceIdRef.current,
          userId,
          event: "oauth_code_submitted",
          projectId,
        });
      }
      setIsSubmittingCode(true);

      try {
        await sendCode(oauthCodeInput.trim());
        handleCompletion();
      } catch (err) {
        setIsSubmittingCode(false);
        setError(err instanceof Error ? err.message : "Failed to submit code");
      }
    }
  }, [oauthCodeInput, userId, sendCode, handleCompletion, projectId]);

  const renderStepContent = () => {
    switch (modalStep) {
      case "info":
        return <InfoStep />;

      case "subscription-check":
        return (
          <SubscriptionCheckStep
            onNoClick={handleSubscriptionNo}
            onYesClick={handleSubscriptionYes}
          />
        );

      case "configuring-auth":
        return <ConfiguringAuthStep />;

      case "oauth-auth": {
        return (
          <OAuthAuthStep
            detectedOAuthUrl={detectedOAuthUrl}
            isCompletingSetup={isSubmittingCode}
            errorMessage={error}
            oauthToken={oauthCodeInput}
            setupStatus={
              isSubmittingCode ? "submitting-code" : "waiting-for-auth"
            }
            onOauthTokenChange={setOauthCodeInput}
            onSubmitCode={handleSubmitCode}
          />
        );
      }

      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (modalStep) {
      case "info":
        return (
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() =>
                window.open(
                  "https://tempolabsinc.mintlify.app/Claude%20Code/unlimited-prompts",
                  "_blank",
                )
              }
            >
              Learn More
            </Button>
            <Button variant="primary" onClick={handleGetStarted}>
              Get Started
            </Button>
          </DialogFooter>
        );

      case "subscription-check":
        return (
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleSubscriptionNo}
              className="flex-1 font-normal"
            >
              No
            </Button>
            <Button
              variant="primary"
              onClick={handleSubscriptionYes}
              className="flex-1 font-normal"
            >
              Yes
            </Button>
          </DialogFooter>
        );

      case "configuring-auth":
        return null;

      case "oauth-auth":
        return null;

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        {modalStep === "info" && (
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-lg font-semibold">
              Get Unlimited Prompts with Claude Code
            </DialogTitle>
          </DialogHeader>
        )}
        {modalStep === "subscription-check" && (
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-lg font-semibold">
              Are you subscribed to Claude Pro/Max?
            </DialogTitle>
          </DialogHeader>
        )}
        {modalStep === "oauth-auth" && (
          <DialogHeader className="space-y-3">
            <DialogTitle>Set Up Claude Code</DialogTitle>
            <DialogDescription>
              Configure Claude Code authentication to enable advanced AI
              assistance.
            </DialogDescription>
          </DialogHeader>
        )}

        {renderStepContent()}
        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
};

export default ClaudeCodeSetupModal;
